import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { Supply } from '../api/types'
import {
  Amt,
  Card,
  DateChip,
  Dt,
  Empty,
  Note,
  NumberInput,
  PageHead,
  Wt,
} from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'
import { useVendorPicker } from '../hooks/usePickers'
import { formatMoney, today } from '../lib/format'

/**
 * Recording a delivery.
 *
 * The rate box may be left empty, and that is not a half-finished entry. Flowers
 * arrive before a price is agreed more often than not, and the delivery then
 * carries a "ریٹ باقی" pill until the lot has sold and an average is applied.
 * Writing a zero instead would tell the book the grower is owed nothing.
 */
export function SupplyEntry() {
  const vendors = useVendorPicker()
  const { isOwner } = useAuth()
  const [vendorId, setVendorId] = useState<string | null>(null)

  /*
   * The counter writes the weight down as the sacks come through the door, and
   * that is the whole of its business here. The list underneath is not: it
   * carries the rate, what the delivery was worth and the commission on it -
   * the terms between the shop and the grower. Not fetched for a STAFF account,
   * so there is nothing to hide and nothing to refuse.
   */
  const recent = useApi<Supply[]>(
    isOwner && vendorId ? `/supplies/by-vendor/${vendorId}` : null,
  )

  const [date, setDate] = useState(today())
  const [weight, setWeight] = useState('')
  const [rate, setRate] = useState('')
  const [kiraya, setKiraya] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  /*
   * Two lookups, and they are not the same thing.
   *
   * <p>The name comes from the picker's own list, which every account has. The
   * terms come from the balances list, which the server sends only to the
   * owner - so for the counter this is undefined, and the card must still be
   * able to say who was chosen. Reading the name off the balances alone left
   * Nizam looking at «زمیندار چنیں» after he had already picked somebody.
   */
  const chosen = vendors.people.find((person) => person.id === vendorId)
  const vendor = vendors.rows.find((row) => row.vendorId === vendorId)

  /*
   * What this delivery comes to, worked out the way the server will work it
   * out: rounded to the rupee from the weight and the rate. Null when there is
   * no rate yet, which is a real state and not a missing number.
   */
  const amount = (() => {
    if (weight === '' || rate === '') return null
    const w = Number(weight)
    const r = Number(rate)
    if (!Number.isFinite(w) || !Number.isFinite(r)) return null
    return Math.round(w * r)
  })()

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    try {
      await api.post('/supplies', {
        date,
        vendorId,
        weight,
        rate: rate === '' ? null : rate,
        kiraya: kiraya || '0',
      })
      setDone(rate === '' ? 'سپلائی لکھ دی — ریٹ بعد میں لگے گا۔' : 'سپلائی لکھ دی۔')
      setWeight('')
      setRate('')
      setKiraya('')
      recent.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <>
      <PageHead
        title="سپلائی"
        hint="زمیندار سے آیا ہوا مال۔"
        aside={<DateChip value={date} onChange={setDate} />}
      />

      <Card>
        {error && <Note kind="error">{error}</Note>}
        {done && <Note kind="info">{done}</Note>}

        <form onSubmit={submit}>
          {/*
            Whose mال it is, first - and with it the two terms that are about to
            be frozen onto this delivery. They used to sit in a sentence below
            the save button, which is after the moment they could still change
            anybody's mind.
          */}
          <div className="buyer">
            {chosen ? (
              <span className="buyer__who">
                <b>{chosen.name}</b>
                {vendor && (
                  <span className="buyer__terms">
                    کمیشن <b>{vendor.commissionPct}%</b>
                    {vendor.qism ? (
                      <>
                        {' · '}قسم <b>{vendor.qism}</b>
                      </>
                    ) : null}
                  </span>
                )}
              </span>
            ) : (
              <span className="buyer__who muted">زمیندار چنیں</span>
            )}

            <PersonPicker
              label=""
              noun="زمیندار"
              vendor
              triggerLabel={chosen ? 'بدلیں' : undefined}
              value={vendorId}
              onChange={setVendorId}
              people={vendors.people}
              recentIds={vendors.recentIds}
              loading={vendors.loading}
            />
          </div>

          {/*
            The weight, and the rate when there is one. The counter writes the
            weight and nothing else - the rate and the carriage are the grower's
            terms, and the server drops them even if they somehow arrive - so it
            gets one wide box rather than half of a pair.
          */}
          <div className={isOwner ? 'two-up' : undefined}>
            <label className="big-field">
              <span className="field-head">وزن (کلو)</span>
              <NumberInput value={weight} onChange={setWeight} decimals placeholder="0.000" />
            </label>

            {isOwner && (
              <label className="big-field">
                <span className="field-head">ریٹ (فی کلو)</span>
                <NumberInput value={rate} onChange={setRate} placeholder="ریٹ باقی" />
              </label>
            )}
          </div>

          {/*
            What the delivery comes to, or that it has no price yet.

            <p>An empty rate is not a half-filled form - flowers arrive before a
            price is agreed more often than not - so the plate says so in words
            instead of showing a nought, which would read as a delivery worth
            nothing.
          */}
          {isOwner && weight !== '' && (
            <div className="supply-total">
              {amount === null ? (
                <>
                  <span>ریٹ بعد میں لگے گا</span>
                  <b className="is-waiting">ریٹ باقی</b>
                </>
              ) : (
                <>
                  <span>مال کی رقم</span>
                  <b>{formatMoney(amount)}</b>
                </>
              )}
            </div>
          )}

          {isOwner && (
            <label className="side-field">
              <span>کرایہ</span>
              <NumberInput value={kiraya} onChange={setKiraya} placeholder="0" />
            </label>
          )}

          <button className="btn btn--save" disabled={!vendorId || !weight}>
            سپلائی محفوظ کریں
          </button>
        </form>
      </Card>

      {isOwner && vendorId && (
        <Card title={`${vendor?.name ?? ''} کی پچھلی سپلائی`}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>وزن</th>
                  <th>ریٹ</th>
                  <th>رقم</th>
                  <th>کرایہ</th>
                  <th>کمیشن</th>
                </tr>
              </thead>
              <tbody>
                {(recent.data ?? []).map((supply) => (
                  <tr key={supply.id}>
                    <td>
                      <Dt value={supply.date} />
                    </td>
                    <td className="num">
                      <Wt value={supply.weight} />
                    </td>
                    <td className="num">
                      {supply.awaitingRate ? (
                        <span className="pill pill--wait">ریٹ باقی</span>
                      ) : (
                        <Amt value={supply.rate} />
                      )}
                    </td>
                    <td className="num">
                      {supply.amount ? <Amt value={supply.amount} /> : '—'}
                    </td>
                    <td className="num">
                      <Amt value={supply.kiraya} tone="muted" />
                    </td>
                    <td className="num">
                      <span className="amt">{supply.commissionPct}%</span>
                    </td>
                  </tr>
                ))}

                {(recent.data?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <Empty>اِس زمیندار کی کوئی سپلائی نہیں۔</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}
